/**
 * Helpers para o campo de valor monetário em pt-BR (usado nos Recibos).
 *
 * O usuário digita no formato brasileiro ("1.234,56"), mas o banco guarda
 * numeric(14,2) — que o PostgREST devolve como número (250.5) ou string
 * ("250.50"), sempre com PONTO decimal. Reidratar o campo com o valor cru e
 * reparsá-lo corrompe o número (o ponto decimal viraria separador de milhar:
 * 250.50 → 25050). Estes dois helpers fecham o round-trip com segurança.
 */

/** Converte a entrada em pt-BR ("1.234,56") para número. Tolera número puro. */
export function parseBRLInput(v: string | number | null | undefined): number {
  // Nunca NEGATIVO. Os dois campos que usam este parser são valor de RECIBO
  // (dinheiro recebido) e frete/desconto do orçamento — em nenhum deles o
  // negativo tem significado, e antes `'-50'` passava direto. Clampar aqui
  // impede que o sinal atravesse até a gravação.
  const clamp = (n: number) => (Number.isFinite(n) ? Math.max(0, n) : 0);

  if (typeof v === 'number') return clamp(v);
  return clamp(
    Number(
      String(v ?? '')
        .replace(/[^\d.,-]/g, '')
        .replace(/\./g, '')
        .replace(',', '.'),
    ),
  );
}

/**
 * Converte um valor numérico (number 250.5 ou string de banco "250.50") no texto
 * pt-BR que o input espera, para que parseBRLInput faça o round-trip correto.
 * Sem a troca '.'→',' o parse trataria o ponto decimal como separador de milhar
 * e inflaria o valor (250.50 → 25050).
 */
export function numberToBRLInput(amount: number | string | null | undefined): string {
  return amount == null || amount === '' ? '' : String(amount).replace('.', ',');
}

/**
 * ─── MÁSCARA DE MOEDA ───────────────────────────────────────────────────────
 *
 * `parseBRLInput` resolve "1.500,00", mas não resolve a AMBIGUIDADE de quem
 * digita só "30.50": em pt-BR o ponto é separador de MILHAR (R$ 3.050), e no
 * teclado do celular ele costuma sair como decimal (R$ 30,50). Nenhum parser
 * adivinha a intenção — os dois palpites erram em silêncio, um por ÷1000 e o
 * outro por ×100, e num campo de preço isso vira produto vendido pelo valor
 * errado sem ninguém ver.
 *
 * A máscara mata a ambiguidade na ORIGEM: não existe separador para digitar. Os
 * dígitos entram pela direita, em centavos, e o campo se formata sozinho —
 *
 *     1 → 0,01 · 15 → 0,15 · 150 → 1,50 · 1500 → 15,00 · 150000 → 1.500,00
 *
 * É idempotente de propósito: a cada tecla ela recebe de volta o próprio texto
 * já formatado (`"1.500,00"`), do qual só os dígitos importam.
 */
export function maskBRLInput(raw: string | number | null | undefined): string {
  // Teto de 15 dígitos: acima disso o número perde precisão em ponto flutuante,
  // e nenhum preço deste app chega perto.
  const digits = String(raw ?? '').replace(/\D/g, '').slice(0, 15).replace(/^0+(?=\d)/, '');
  if (!digits) return '';
  return (Number(digits) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Hidrata um campo mascarado a partir do número do banco. Par obrigatório de
 * `maskBRLInput`/`parseBRLInput`: formatar de um jeito e ler de outro corrompe o
 * valor na primeira edição (a lição do #153a).
 *
 * Ausência continua VAZIA — `null` é "não informado", que não é zero.
 */
export function numberToMaskedBRL(amount: number | string | null | undefined): string {
  if (amount == null || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return maskBRLInput(String(Math.round(Math.abs(n) * 100)));
}
